import { Box, BoxProps } from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback, useState } from 'react'

import { Quote } from '../../../domain'
import { usePromotion } from '../../../hooks/covers'
import { useQuotePremiums } from '../../../hooks/premiums'
import { TKey } from '../../../infrastructure/internationalisation'
import { ProductConfig } from '../../../state/configuration'
import {
  StandardAdjustedPremium,
  StandardAdjustedPremiumMonthly,
  StandardAdjustedPremiumTerm,
} from './standard'

export interface AnnualAdjustedPremiumsProps extends BoxProps {
  currentQuote: Quote
  previousQuote?: Quote
  product: ProductConfig
  expanded?: boolean
}

const tKey = TKey('components.annual-adjusted-premium')

export const AnnualAdjustedPremium: FunctionComponent<AnnualAdjustedPremiumsProps> = ({
  currentQuote,
  previousQuote,
  product,
  expanded,
  ...props
}) => {
  const currentPremiums = useQuotePremiums(currentQuote, product)
  const previousPremiums = useQuotePremiums(previousQuote, product)

  const promotion = usePromotion(currentQuote)

  const [isExpanded, setIsExpanded] = useState(expanded ?? false)
  const onExpansionChange = useCallback(setIsExpanded, [expanded])

  const Component = (() => {
    if (!product.payment?.schedules) return StandardAdjustedPremium

    switch (currentPremiums?.paymentSchedule) {
      case 'MONTHLY':
        return StandardAdjustedPremiumMonthly
      case 'SINGLE_PAYMENT':
        return StandardAdjustedPremiumTerm
      default:
        return null
    }
  })()

  return Component ? (
    <Box width={1} p={2} {...props}>
      <Component
        currentPremiums={currentPremiums}
        previousPremiums={previousQuote && previousPremiums}
        promotion={promotion}
        expanded={isExpanded}
        tKey={tKey}
        onExpansionChange={onExpansionChange}
      />
    </Box>
  ) : null
}
